import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  getMigrationStates,
  readsFromErebus as statesReadFromErebus,
  writesToErebus as statesWriteToErebus,
  type MigrationState,
  type MigrationStatus,
} from '@erebus/http';

/**
 * Where the console reads and writes each sync-managed entity (Erebus or
 * legacy), from GET /migration/states. Loaded once per session; call
 * `refresh()` when the backend says the state has changed (409 `sync_managed`).
 *
 * Not the admin store for changing states — that is migrationStateStore.
 */
export const useMigrationStatusStore = defineStore('migrationStatus', () => {
  // Null until loaded. Until then every entity reads and writes legacy.
  const states = ref<MigrationState[] | null>(null);
  // The load in flight, so callers asking at the same time share one request.
  let pending: Promise<void> | null = null;

  /** Fetches the states, replacing what is held. Errors pass through. */
  const refresh = async (): Promise<void> => {
    states.value = await getMigrationStates();
  };

  /** Loads the states unless they are already loaded. Throws when they cannot be loaded. */
  const load = async (): Promise<void> => {
    if (states.value) return;
    pending ??= refresh().finally(() => {
      pending = null;
    });
    await pending;
  };

  /** True when the console should read the entity from Erebus. */
  const readsFromErebus = (entity: string): boolean =>
    states.value !== null && statesReadFromErebus(states.value, entity);

  /** True when the console should write the entity to Erebus. */
  const writesToErebus = (entity: string): boolean =>
    states.value !== null && statesWriteToErebus(states.value, entity);

  /** The entity's status, or undefined until loaded or when it has no row. */
  const statusOf = (entity: string): MigrationStatus | undefined =>
    states.value?.find((s) => s.entity === entity)?.status;

  return { load, refresh, readsFromErebus, writesToErebus, statusOf };
});
